import { produce } from 'immer';
import { SetState, create } from 'zustand';
import {
  ActiveEvaluationState,
  ChatBooleanState,
  CommonChatAction,
  CommonChatState,
  CommonChatStore,
  LastChatEvaluationState,
} from '~/types';

const initialChatBooleanState = (): ChatBooleanState => ({
  showLeaveRoomOpen: false,
  showSubMenu: false,
  showCharacter: true,
  showHistory: false,
  isChatLayoutShow: true,
  isTextFocus: false,
  isWaitingChatRoomInfo: false,
  isReportOpen: false,
});

const initialCommonChatState = (): CommonChatState => ({
  roomType: null,
  chatName: '',
  chatMembers: [],
  avatarInfo: null,
  language: '',
  isNotificationOn: false,
  isSafeChatOn: false,
  background_url: [],
  assistant_line: [],
  case_note: {
    Background: '',
    Mission: '',
    'Key Characters': '',
    Clue: '',
  },
  step: null,
});

const initialLastChatEvaluation = (): LastChatEvaluationState => ({
  id: '',
  send_time: '',
  like: null,
});

const initActiveEvaluation = (): ActiveEvaluationState => ({
  activeEvaluation: null,
});

const commonChatActions = (
  set: SetState<CommonChatStore>,
  get: () => CommonChatStore,
): CommonChatAction => ({
  setShowLeaveRoomOpen: (value) => {
    set(
      produce((state: ChatBooleanState) => {
        state.showLeaveRoomOpen = value;
      }),
    );
  },

  setShowSubMenu: (showSubMenu) => {
    set(
      produce((state: ChatBooleanState) => {
        state.showSubMenu = showSubMenu;
      }),
    );
  },
  setShowCharacter: (showCharacter) => {
    set(
      produce((state: ChatBooleanState) => {
        state.showCharacter = showCharacter;
      }),
    );
  },
  setShowHistory: (state: boolean | ((prev: boolean) => boolean)) => {
    set(
      produce((draft: ChatBooleanState) => {
        if (typeof state === 'function') {
          draft.showHistory = state(draft.showHistory);
        } else {
          draft.showHistory = state;
        }
      }),
    );
  },
  setIsChatLayoutShow: (isChatLayoutShow) => {
    set(
      produce((state: ChatBooleanState) => {
        state.isChatLayoutShow = isChatLayoutShow;
      }),
    );
  },
  setIsTextFocus: (isTextFocus) => {
    set(
      produce((state: ChatBooleanState) => {
        state.isTextFocus = isTextFocus;
      }),
    );
  },
  setChatRoomInfo: (info) => {
    set(
      produce((state: CommonChatState) => {
        Object.assign(state, info);
      }),
    );
  },
  setSaveLastChatEvaluation: (info) => {
    set(
      produce((state: CommonChatState) => {
        Object.assign(state, info);
      }),
    );
  },
  setIsNotificationOn: (isNotificationOn) => {
    set(
      produce((state: CommonChatState) => {
        state.isNotificationOn = isNotificationOn;
      }),
    );
  },
  setIsSafeChatOn: (on) => {
    set(
      produce((state: CommonChatState) => {
        state.isSafeChatOn = on;
      }),
    );
  },
  setIsWaitingChatRoomInfo: (isWaitingChatRoomInfo) => {
    set(
      produce((state: ChatBooleanState) => {
        state.isWaitingChatRoomInfo = isWaitingChatRoomInfo;
      }),
    );
  },
  setActiveEvaluation: (
    evaluation:
      | { id: string; send_time: string }
      | null
      | ((
          prevState: { id: string; send_time: string } | null,
        ) => { id: string; send_time: string } | null),
  ) => {
    set(
      produce((state: ActiveEvaluationState) => {
        if (typeof evaluation === 'function') {
          state.activeEvaluation = evaluation(state.activeEvaluation);
        } else {
          state.activeEvaluation = evaluation;
        }
      }),
    );
  },

  updateChatMembers: ({ type, email, nickname }) => {
    set(
      produce((state: CommonChatState) => {
        if (type === 'enter') {
          const target = get().chatMembers.some((el) => el.id === email);
          if (!target)
            state.chatMembers.push({
              id: email,
              name: nickname,
              is_blocked: false,
            });
        } else if (type === 'leave') {
          state.chatMembers = state.chatMembers.filter(
            (prev) => prev.id !== email,
          );
        }
      }),
    );
  },

  updateBlockedMember: ({ block_user_id, is_blocked }) => {
    set(
      produce((state: CommonChatState) => {
        const member = state.chatMembers.find(
          (member) => member.id === block_user_id,
        );
        if (member) {
          member.is_blocked = is_blocked;
        }
      }),
    );
  },
  setIsReportOpen: (isReportOpen) => {
    set(
      produce((state: ChatBooleanState) => {
        state.isReportOpen = isReportOpen;
      }),
    );
  },

  resetAll: () =>
    set({ ...initialCommonChatState(), ...initialChatBooleanState() }),
});

const useCommonChatStore = create<CommonChatStore>()((set, get) => ({
  ...initialChatBooleanState(),
  ...initialCommonChatState(),
  ...initialLastChatEvaluation(),
  ...initActiveEvaluation(),
  ...commonChatActions(set, get),
}));

export default useCommonChatStore;
